// entities/institution/model/useAppearance.ts
//
// Чтение конфига внешнего вида витрины из настроек заведения.
// Источник — institution.visual.params_json.appearance, куда его пишет
// раздел «Внешний вид» в /administration/settings.
//
// Композабл намеренно без собственного состояния: только computed поверх стора.
// Модульный singleton-ref здесь дал бы detached scope и остановившиеся watch'и
// (см. shared/lib — та же грабля уже ловилась в других композаблах).

import { computed, type ComputedRef } from 'vue';
import { useInstitutionStore } from './useInstitutionStore';
import {
  DEFAULT_APPEARANCE,
  readAppearanceFromParams,
  type AppearanceCatalogConfig,
  type AppearanceConfig,
  type AppearanceSurfaceKey,
} from '@shared/config';
import { isNativePlatform } from '@shared/lib';

export interface UseAppearanceResult {
  /** Полный нормализованный конфиг. Всегда валиден — при пустых настройках это дефолты. */
  appearance: ComputedRef<AppearanceConfig>;
  /** Текущая среда: в нативной сборке — `app`, в браузере — `site`. */
  surface: ComputedRef<AppearanceSurfaceKey>;
  /** Настройки каталога для текущей среды. */
  catalog: ComputedRef<AppearanceCatalogConfig>;
}

export const useAppearance = (): UseAppearanceResult => {
  const institutionStore = useInstitutionStore();

  const appearance = computed<AppearanceConfig>(() => {
    const params = (institutionStore.institution as any)?.visual?.params_json;
    // До первой загрузки заведения params ещё нет — отдаём дефолты, чтобы каталог
    // не мигал «пустым» шаблоном и не ломал SSR-разметку.
    if (!params) return DEFAULT_APPEARANCE;
    return readAppearanceFromParams(params);
  });

  const surface = computed<AppearanceSurfaceKey>(() => (isNativePlatform() ? 'app' : 'site'));

  const catalog = computed<AppearanceCatalogConfig>(() => appearance.value.catalog[surface.value]);

  return { appearance, surface, catalog };
};
